import type { replayIntegration } from '@sentry/react'
import type { BeforeSendFn } from 'posthog-js'

import {
  hasTokenPath,
  redactTokenPaths,
  redactTokenPathsDeep,
} from '@beaded/shared-lib/src/redactSecrets.js'

// /reset/<token> and /magic/<token> (and the API's /user/once/<token> they
// call) carry a live one-time credential in the URL. Everything that ships
// URLs out of the browser -- analytics pageviews, PostHog autocapture,
// Sentry replay -- goes through these helpers.

type ReplayOptions = NonNullable<Parameters<typeof replayIntegration>[0]>
type BeforeAddRecordingEvent = NonNullable<
  ReplayOptions['beforeAddRecordingEvent']
>

/**
 * Sentry Replay is not started on a token page: its meta events (page href)
 * and the replay_event `urls` list bypass beforeSend AND
 * beforeAddRecordingEvent (that hook only sees custom events), so the only
 * way to keep the token out is not to record.
 */
export const replayAllowedOn = (pathname: string): boolean =>
  !hasTokenPath(pathname)

/** Replay custom events (breadcrumbs, network / navigation spans). */
export const sentryBeforeAddRecordingEvent: BeforeAddRecordingEvent = (event) =>
  redactTokenPathsDeep(event)

/** PostHog: $current_url, $pathname, $referrer and every other property. */
export const posthogBeforeSend: BeforeSendFn = (cr) =>
  cr ? redactTokenPathsDeep(cr) : cr

/** Values for a pageview hit, with any one-time token redacted. */
export const analyticsPageview = (
  pathname: string,
  searchStr: string,
  href: string,
) => {
  const path = redactTokenPaths(pathname)
  return {
    path,
    pathWithSearch: `${path}${searchStr}`,
    href: redactTokenPaths(href),
  }
}

/**
 * replayAllowedOn only decides at page load. If the SPA ever navigates into
 * a token route, stop the running replay before the page records.
 */
export const stopReplayOnTokenRoute = (
  pathname: string,
  getReplay: () => { stop: () => Promise<void> } | undefined,
): void => {
  if (!hasTokenPath(pathname)) return
  void getReplay()?.stop()
}
